/**
 * @file BlendMaterialHistoryModal.js
 * @version 1.0.0
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-08-17
 * @modified 2026-08-17
 * @description One raw material's price history, one price column at a time.
 *
 *              Reuses CostPriceHistoryChart unchanged — it is purely
 *              presentational and takes the same { points, current_cost } shape
 *              that GET /api/inventory/:id/cost-prices already returns, so the
 *              new endpoint was written to that contract rather than the chart
 *              being adapted to a new one.
 *
 *              Periods where a material wasn't quoted are absent from the series
 *              rather than plotted at zero (enforced server-side). A £0 trough
 *              would read as a price collapse instead of a gap in the data.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { API_URL } from '../config';
import Modal from './Modal';
import Dropdown from './Dropdown';
import CostPriceHistoryChart from './CostPriceHistoryChart';
import { formatCurrency } from '../utils/currencyFormatter';
import './BlendCalculator.css';
import './SearchInventory.css';   // .cost-history-chart sizing lives here

const ROLE_LABEL = { spot: 'SPOT', current: 'CURRENT', upcoming: 'UPCOMING' };

export default function BlendMaterialHistoryModal({ material, columns = [], onClose }) {
  const [columnKey, setColumnKey] = useState(columns[0]?.key || null);
  const [data, setData] = useState({ points: [], current_cost: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const options = columns.map((c) => ({
    value: c.key,
    label: ROLE_LABEL[c.role]
      ? `${ROLE_LABEL[c.role]}${c.period_name ? ` — ${c.period_name}` : ''}`
      : c.label
  }));

  const load = useCallback(async () => {
    if (!material?.id || !columnKey) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/blends/materials/${material.id}/cost-history?column=${encodeURIComponent(columnKey)}`,
        { credentials: 'include' });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Could not load the price history');
      setData({ points: d.points || [], current_cost: d.current_cost ?? null });
      setError('');
    } catch (e) {
      setError(e.message);
      setData({ points: [], current_cost: null });
    } finally {
      setLoading(false);
    }
  }, [material, columnKey]);

  useEffect(() => { load(); }, [load]);

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={`${material?.name || 'Material'} — price history`}
      size="md"
      className="blend-material-history"
    >
      <div className="blend-history-body">
        {error && (
          <div className="blend-warning inline">
            <span className="material-symbols-rounded">error</span>
            <span>{error}</span>
          </div>
        )}

        <div className="filter-group">
          <label>Price column</label>
          <Dropdown options={options} value={columnKey} onChange={setColumnKey} />
        </div>

        {loading && (
          <div className="loading">
            <span className="material-symbols-rounded spinning">progress_activity</span>
            <span>Loading…</span>
          </div>
        )}

        {!loading && !error && (
          <>
            <div className="blend-history-latest">
              <span className="blend-history-label">Latest recorded</span>
              <span className="blend-history-value">
                {data.current_cost !== null ? `${formatCurrency(data.current_cost)}/T` : '—'}
              </span>
              <span className="blend-history-label">
                {data.points.length} recorded change{data.points.length === 1 ? '' : 's'}
              </span>
            </div>

            {/* The chart returns null below two points, so say why rather than
                leaving an unexplained gap where a chart should be. */}
            {data.points.length >= 2 ? (
              <CostPriceHistoryChart points={data.points} currentCost={data.current_cost} />
            ) : (
              <p className="blend-book-empty">
                {data.points.length === 1
                  ? 'Only one price has been recorded for this column, so there is no trend to plot yet.'
                  : 'No prices recorded for this column.'}
              </p>
            )}
          </>
        )}
      </div>
    </Modal>
  );
}
